/** Thin wrapper over the backend REST API (session cookie based). */
import type {
  BlockAnimation,
  BlockCollection,
  CustomBlockDefinition,
  LibraryBlockDefinition,
  TexturePixels,
  VoxelMaterial,
} from '@shared/protocol';

export interface Profile {
  id: string;
  nick: string | null;
  skin: string;
  /** Server-gated; only true for users with users.is_dev. */
  isDev: boolean;
}

export interface WorldZoneInfo {
  index: number;
  ownerId: string | null;
  ownerNick: string | null;
}

export interface WorldInfo {
  id: number;
  name: string;
  zones: WorldZoneInfo[];
}

export interface HealthInfo {
  ok: boolean;
  buildDatetime: string;
}

export interface BlockLibrary {
  collections: BlockCollection[];
  customBlocks: CustomBlockDefinition[];
  libraryBlocks: LibraryBlockDefinition[];
  hotbar: number[];
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(path, {
    ...init,
    headers,
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(res.status, String(body.error ?? `HTTP ${res.status}`));
  }
  return body as T;
}

export const api = {
  health(): Promise<HealthInfo> {
    return request<HealthInfo>('/api/health');
  },

  /** Current profile, or null when there is no valid session. */
  async me(): Promise<Profile | null> {
    try {
      return await request<Profile>('/api/me');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },

  async redeemInvite(token: string): Promise<void> {
    await request('/api/invite', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  async saveProfile(nick: string, skin: string): Promise<void> {
    await request('/api/me', {
      method: 'POST',
      body: JSON.stringify({ nick, skin }),
    });
  },

  worlds(): Promise<WorldInfo[]> {
    return request<WorldInfo[]>('/api/worlds');
  },

  blockLibrary(): Promise<BlockLibrary> {
    return request<BlockLibrary>('/api/block-library');
  },

  createCollection(
    name: string,
    description: string,
    access: BlockCollection['access'],
  ): Promise<BlockCollection> {
    return request<BlockCollection>('/api/collections', {
      method: 'POST',
      body: JSON.stringify({ name, description, access }),
    });
  },

  updateCollection(
    id: number,
    name: string,
    description: string,
    access: BlockCollection['access'],
  ): Promise<BlockCollection> {
    return request<BlockCollection>(`/api/collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name, description, access }),
    });
  },

  async deleteCollection(
    id: number,
    mode: 'keep-blocks' | 'delete-blocks',
  ): Promise<void> {
    await request(`/api/collections/${id}?mode=${mode}`, { method: 'DELETE' });
  },

  createCustomBlock(
    name: string,
    gridSize: number,
    voxels: Array<[number, VoxelMaterial]>,
    noise?: CustomBlockDefinition['noise'],
    collision?: CustomBlockDefinition['collision'],
  ): Promise<CustomBlockDefinition> {
    return request<CustomBlockDefinition>('/api/custom-blocks', {
      method: 'POST',
      body: JSON.stringify({
        name,
        gridSize,
        voxels,
        noise: noise ?? null,
        collision: collision ?? null,
      }),
    });
  },

  updateCustomBlock(
    id: number,
    name: string,
    gridSize: number,
    voxels: Array<[number, VoxelMaterial]>,
    noise?: CustomBlockDefinition['noise'],
    collision?: CustomBlockDefinition['collision'],
    editViewId?: number,
  ): Promise<CustomBlockDefinition> {
    return request<CustomBlockDefinition>(`/api/custom-blocks/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name,
        gridSize,
        voxels,
        ...(noise !== undefined ? { noise } : {}),
        ...(collision !== undefined ? { collision } : {}),
        ...(editViewId !== undefined ? { editViewId } : {}),
      }),
    });
  },

  patchCustomBlock(
    id: number,
    patch: {
      name?: string;
      collectionId?: number | null;
      noise?: CustomBlockDefinition['noise'];
      collision?: CustomBlockDefinition['collision'];
      editViewId?: number;
    },
  ): Promise<CustomBlockDefinition> {
    return request<CustomBlockDefinition>(`/api/custom-blocks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  async deleteCustomBlock(id: number): Promise<void> {
    await request(`/api/custom-blocks/${id}`, { method: 'DELETE' });
  },

  createSolidBlock(
    name: string,
    color: string,
    opacity: number,
    collision?: Extract<LibraryBlockDefinition, { kind: 'solid' }>['collision'],
    glowIntensity?: number | null,
  ): Promise<LibraryBlockDefinition> {
    return request<LibraryBlockDefinition>('/api/library-blocks/solid', {
      method: 'POST',
      body: JSON.stringify({
        name,
        color,
        opacity,
        collision: collision ?? null,
        glowIntensity: glowIntensity ?? null,
      }),
    });
  },

  updateSolidBlock(
    id: number,
    patch: {
      name: string;
      color?: string;
      opacity?: number;
      glowIntensity?: number | null;
      collectionId?: number | null;
      collision?: Extract<LibraryBlockDefinition, { kind: 'solid' }>['collision'];
      editViewId?: number;
    },
  ): Promise<LibraryBlockDefinition> {
    return request<LibraryBlockDefinition>(`/api/library-blocks/${id}/solid`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },

  createTexturedBlock(
    name: string,
    color: string,
    pixels: TexturePixels,
    noise?: Extract<LibraryBlockDefinition, { kind: 'textured' }>['noise'],
    collision?: Extract<LibraryBlockDefinition, { kind: 'textured' }>['collision'],
  ): Promise<LibraryBlockDefinition> {
    return request<LibraryBlockDefinition>('/api/library-blocks/textured', {
      method: 'POST',
      body: JSON.stringify({
        name,
        color,
        pixels,
        noise: noise ?? null,
        collision: collision ?? null,
      }),
    });
  },

  updateTexturedBlock(
    id: number,
    name: string,
    color: string,
    pixels: TexturePixels,
    collectionId?: number | null,
    noise?: Extract<LibraryBlockDefinition, { kind: 'textured' }>['noise'],
    collision?: Extract<LibraryBlockDefinition, { kind: 'textured' }>['collision'],
    editViewId?: number,
  ): Promise<LibraryBlockDefinition> {
    return request<LibraryBlockDefinition>(`/api/library-blocks/${id}/texture`, {
      method: 'PUT',
      body: JSON.stringify({
        name,
        color,
        pixels,
        collectionId,
        ...(noise !== undefined ? { noise } : {}),
        ...(collision !== undefined ? { collision } : {}),
        ...(editViewId !== undefined ? { editViewId } : {}),
      }),
    });
  },

  async deleteLibraryBlock(id: number): Promise<void> {
    await request(`/api/library-blocks/${id}`, { method: 'DELETE' });
  },

  createCustomBlockView(
    id: number,
    name?: string,
    sourceViewId?: number,
  ): Promise<CustomBlockDefinition> {
    return request<CustomBlockDefinition>(`/api/custom-blocks/${id}/views`, {
      method: 'POST',
      body: JSON.stringify({
        ...(name ? { name } : {}),
        ...(sourceViewId !== undefined ? { sourceViewId } : {}),
      }),
    });
  },

  activateCustomBlockView(
    id: number,
    viewId: number,
  ): Promise<CustomBlockDefinition> {
    return request<CustomBlockDefinition>(
      `/api/custom-blocks/${id}/views/${viewId}/activate`,
      { method: 'POST', body: '{}' },
    );
  },

  renameCustomBlockView(
    id: number,
    viewId: number,
    name: string,
  ): Promise<CustomBlockDefinition> {
    return request<CustomBlockDefinition>(
      `/api/custom-blocks/${id}/views/${viewId}`,
      { method: 'PATCH', body: JSON.stringify({ name }) },
    );
  },

  deleteCustomBlockView(
    id: number,
    viewId: number,
  ): Promise<CustomBlockDefinition> {
    return request<CustomBlockDefinition>(
      `/api/custom-blocks/${id}/views/${viewId}`,
      { method: 'DELETE' },
    );
  },

  createLibraryBlockView(
    id: number,
    name?: string,
    sourceViewId?: number,
  ): Promise<LibraryBlockDefinition> {
    return request<LibraryBlockDefinition>(`/api/library-blocks/${id}/views`, {
      method: 'POST',
      body: JSON.stringify({
        ...(name ? { name } : {}),
        ...(sourceViewId !== undefined ? { sourceViewId } : {}),
      }),
    });
  },

  activateLibraryBlockView(
    id: number,
    viewId: number,
  ): Promise<LibraryBlockDefinition> {
    return request<LibraryBlockDefinition>(
      `/api/library-blocks/${id}/views/${viewId}/activate`,
      { method: 'POST', body: '{}' },
    );
  },

  renameLibraryBlockView(
    id: number,
    viewId: number,
    name: string,
  ): Promise<LibraryBlockDefinition> {
    return request<LibraryBlockDefinition>(
      `/api/library-blocks/${id}/views/${viewId}`,
      { method: 'PATCH', body: JSON.stringify({ name }) },
    );
  },

  deleteLibraryBlockView(
    id: number,
    viewId: number,
  ): Promise<LibraryBlockDefinition> {
    return request<LibraryBlockDefinition>(
      `/api/library-blocks/${id}/views/${viewId}`,
      { method: 'DELETE' },
    );
  },

  updateCustomBlockAnimation(
    id: number,
    animation: BlockAnimation,
  ): Promise<CustomBlockDefinition> {
    return request<CustomBlockDefinition>(`/api/custom-blocks/${id}/animation`, {
      method: 'PUT',
      body: JSON.stringify(animation),
    });
  },

  updateLibraryBlockAnimation(
    id: number,
    animation: BlockAnimation,
  ): Promise<LibraryBlockDefinition> {
    return request<LibraryBlockDefinition>(`/api/library-blocks/${id}/animation`, {
      method: 'PUT',
      body: JSON.stringify(animation),
    });
  },

  saveHotbar(hotbar: number[]): Promise<void> {
    return request('/api/hotbar', {
      method: 'PUT',
      body: JSON.stringify({ hotbar }),
    });
  },
};
